import { useQuery, useLazyQuery } from '@apollo/client';
import {
  EXCLUDE_CONTRACT_RECURRING_MODE,
  EXCLUDE_END_DATE_CATEGORIES
} from '@nile/banyan/src/Pages/ServiceCatalog/constants';
import { EndDate } from '@nile/banyan/src/Pebbles/ServiceCatalog/catalogForm';
import {
  AppContext,
  DATE_TIME_FORMAT,
  DropdownIndicator,
  formatGroupLabel,
  getTranslatedText,
  IndicatorSeparator,
  Loader,
  useCurrencySymbols
} from '@nile/components';
import {
  AccountClient,
  GET_SERVICE_ITEMS,
  GET_PRORATED_AMOUNT,
  useCachedQuery
} from '@nile/graphql';
import {
  Checkbox,
  Dropdown,
  Label,
  TextField,
  Heading,
  Button,
  DateTimePicker as DatePicker,
  Badge,
  Spinner
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import dayjs from 'dayjs';
import { cloneDeep, includes, isEmpty, pick } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
// import DatePicker from 'react-datepicker';
import { withLDConsumer, useFlags } from 'launchdarkly-react-client-sdk';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import QuantityCollector from './QuantityCollector';
import SubscriptionSelectField from './SubscriptionSelectField';
import getMinEndDate from './helpers';

const BlockHourTypeParent = styled.div`
  .applicable-in-block {
    margin-top: 20px;
    margin-bottom: 30px;
  }

  .adjust-hours-block {
    margin-left: 20px;
  }

  .value_selector__control {
    width: 200px;
  }
`;

const Field = styled.div`
  margin-top: 24px;
  width: 200px;
  min-width: 200px;
  .item-quantity {
    padding-left: 0 !important;
  }
  .react-datepicker__input-container > input {
    height: 38px;
    width: 200px;
  }
`;

const ProrateWrap = styled.div`
  margin-top: 24px;
  .item-quantity {
    padding-left: 0 !important;
  }
  .react-datepicker__input-container > input {
    height: 38px;
    width: 200px;
  }
`;

const Operation = styled.div`
  background: ${(props) =>
    props.selected
      ? props.type === 'DECREASE'
        ? '#EC5151'
        : '#77BB61'
      : 'transprent'};
  transform: ${(props) =>
    props.type === 'DECREASE' ? 'rotate(0deg)' : 'rotate(180deg)'};
`;

/**
 * Section to be rendered for getting block hours configuration
 * @public
 */

function NewBlockhourTypeSection(props) {
  const { company } = useContext(AppContext);
  const currencyCode = company?.currency || 'USD';
  const countryCode = company?.countryCode || 'US';
  const {
    intl,
    serviceItem,
    actions,
    errors,
    handlePropChange,
    handlePropertiesChange,
    subscriptionTypes,
    customSubscriptionTypes,
    configurableSections,
    applicableBlockHourTypes,
    isDisabled,
    isEditEndDatedisabled,
    clientId,
    formType,
    enableReminder,
    nextBillDate,
    nextBillDateLoading
  } = props;
  const {
    name,
    canEdit,
    invoiceDescription,
    category,
    quantity,
    quantityType,
    roundUpValue,
    unitPrice,
    // subscriptionType,
    frequency,
    useAsWorklogItem,
    businessHoursUnitPrice,
    afterHoursUnitPrice,
    blockItemUsedIn,
    effectiveDate,
    billDate,
    endDate,
    autoRenew,
    selectedBlockItemAdjustedItems,
    carryForwardRemainingBlockHours,
    adjustBlockItemAgainstAllItems,
    changes,
    change,
    remindDuration
  } = serviceItem;

  const subscriptionType = frequency?.type;

  const { currencySymbolWithCode } = useCurrencySymbols();

  const flags = useFlags();

  const { loading, data, error } = useCachedQuery(GET_SERVICE_ITEMS, {
    client: AccountClient,
    variables: {
      listInfo: {
        condition: {
          joinOperator: 'AND',
          operands: [
            {
              attribute: 'quantityType',
              operator: 'is',
              value: 'HOURS'
            }
          ]
        }
      }
    }
  });
  const [proratedAmount, setProratedAmount] = useState(0);

  const [
    getProratedAmount,
    { loading: proratedLoading, data: proratedData, error: proratedError }
  ] = useLazyQuery(GET_PRORATED_AMOUNT, {
    client: AccountClient,
    onCompleted(response) {
      if (response) {
        setProratedAmount(response?.getProratedAmount);
      }
    },
    fetchPolicy: 'cache-and-network'
  });

  if (loading) {
    return (
      <div className="mt-40">
        <Loader />
      </div>
    );
  }
  const adjustItems = data?.getServiceItems?.items?.map((el) => {
    return {
      ...el,
      value: el.name,
      label: el.name
    };
  });
  const adjustBlockHourItems = (val) => {
    const updatedData = cloneDeep(selectedBlockItemAdjustedItems) || [];
    const isItemPresent = updatedData.some((el) => el.itemId === val.itemId);
    if (!isItemPresent) {
      updatedData.push({
        itemId: val.itemId,
        name: val.name
      });
    }
    handlePropChange('selectedBlockItemAdjustedItems', updatedData);
  };
  const deleteAdjustedItem = (val) => {
    const updatedData = cloneDeep(selectedBlockItemAdjustedItems) || [];
    const index = updatedData.findIndex((el) => el.itemId === val.itemId);
    updatedData.splice(index, 1);
    handlePropChange('selectedBlockItemAdjustedItems', updatedData);
  };
  const renderQuantityGrid = () => {
    if (!changes?.length) {
      if (!change) {
        handlePropChange('change', { quantity: quantity });
      }
      return (
        <QuantityCollector
          mode="DEFAULT"
          change={change}
          handlePropChange={handlePropChange}
          effectiveDate={effectiveDate}
          billDate={billDate}
          nextBillDate={nextBillDate}
          endDate={endDate}
          intl={intl}
          currencyCode={currencyCode}
          countryCode={countryCode}
          subscriptionType={frequency?.unit}
          frequency={frequency}
          unitPrice={unitPrice}
          getProratedAmount={getProratedAmount}
          proratedAmount={proratedAmount}
          loading={proratedLoading}
          sectionType="BLOCK"
          dynamicType="HOUR"
          quantityType={quantityType}
          clientId={clientId}
          formType={formType}
          quantity={quantity}
          errors={props.errors}
        />
      );
    }
  };

  let nextBillDateBasedOnBillingType = nextBillDate;

  if (
    nextBillDateBasedOnBillingType &&
    frequency?.recurringMode === 'ADVANCE'
  ) {
    nextBillDateBasedOnBillingType = dayjs(nextBillDate).add(
      frequency?.recurringBefore,
      'days'
    );
  }

  return (
    <BlockHourTypeParent>
      <div className="flex g20 w400">
        <Field>
          <Label>
            <FormattedMessage id="admin.serviceCatalog.costPerHour" />
          </Label>
          <div className="flex actr mb-10">
            <TextField
              size="default"
              value={unitPrice}
              min={0}
              type="number"
              onChange={(val) => {
                handlePropChange('unitPrice', val);
              }}
              onBlur={() => {
                if (change?.effectiveDate) {
                  getProratedAmount({
                    variables: {
                      itemInput: {
                        quantityType: quantityType,
                        billDate: billDate,
                        effectiveDate: change?.effectiveDate,
                        unitPrice: unitPrice,
                        quantity: change?.quantity
                          ? change?.quantity
                          : undefined,
                        // subscriptionType: subscriptionType,
                        frequency: frequency,
                        endDate: endDate || undefined,
                        clientId: clientId
                      }
                    },
                    input: {
                      itemInput: {
                        quantityType: quantityType,
                        billDate: billDate,
                        effectiveDate: change?.effectiveDate,
                        unitPrice: unitPrice,
                        quantity: change?.quantity
                          ? change?.quantity
                          : undefined,
                        // subscriptionType: subscriptionType,
                        frequency: frequency,
                        endDate: endDate || undefined,
                        clientId: clientId
                      }
                    }
                  });
                }
              }}
              disabled={isDisabled}
              suffix={currencySymbolWithCode}
            />
          </div>
        </Field>
        <Field>
          <Label>
            <FormattedMessage id="admin.serviceCatalog.totalCost" />
          </Label>
          <div className="flex actr mb-10">
            <TextField
              size="default"
              value={
                formType === 'service-catalog' ||
                formType === 'contract-template'
                  ? (unitPrice * quantity)?.toString() || ''
                  : (
                      unitPrice *
                      (changes
                        ? changes?.[0]?.quantity
                        : isEmpty(change)
                        ? quantity
                        : change?.quantity)
                    )?.toString() || ''
              }
              min={0}
              type="number"
              onChange={(val) => {
                handlePropChange('unitPrice', val);
              }}
              disabled
              suffix={currencySymbolWithCode}
            />
          </div>
        </Field>
        <Field>
          <Label>
            <FormattedMessage id="admin.serviceCatalog.blockHours.applicableIn" />
          </Label>
          <Dropdown
            isClearable
            name="applicableIn"
            value={applicableBlockHourTypes.find(
              (el) => el.name === blockItemUsedIn
            )}
            onChange={(val) => {
              handlePropChange('blockItemUsedIn', val?.name || null);
            }}
            options={applicableBlockHourTypes}
            components={{
              IndicatorSeparator,
              DropdownIndicator
            }}
            classNamePrefix="value_selector"
            formatGroupLabel={formatGroupLabel}
            menuPlacement="auto"
            placeholder={getTranslatedText(
              intl,
              'admin.serviceCatalog.applicableIn.placeholder'
            )}
            closeMenuOnScroll={(e) => {
              return (
                e.target &&
                e.target.classList &&
                !(e.target.classList.value.indexOf('menu-list') > -1)
              );
            }}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 })
            }}
            isDisabled={isDisabled}
          />
        </Field>
      </div>
      <div className="col g20 width-600">
        {configurableSections.indexOf('subscriptionType') > -1 && (
          <SubscriptionSelectField
            intl={intl}
            serviceItem={serviceItem}
            isDisabled={isDisabled}
            handlePropChange={handlePropChange}
            handlePropertiesChange={handlePropertiesChange}
            subscriptionTypes={subscriptionTypes}
            customSubscriptionTypes={customSubscriptionTypes}
          />
        )}

        <div className="fx actr">
          {configurableSections.indexOf('effectiveDate') > -1 ? (
            <Field>
              <div>
                <Label>
                  <FormattedMessage id="client.details.contract.effectiveDate" />
                </Label>
                <DatePicker
                  selected={billDate && dayjs(billDate).toDate()}
                  onChange={(date) => {
                    const formattedDate = date
                      ? dayjs(date).format('YYYY-MM-DD')
                      : null;
                    const inputObj = {};
                    if (change) {
                      inputObj.change = {
                        ...change,
                        effectiveDate: null,
                        quantity: change?.quantity || null
                      };
                    }
                    handlePropertiesChange({
                      billDate: formattedDate,
                      endDate: null,
                      ...inputObj
                    });
                  }}
                  placeholderText="YYYY-MM-DD"
                  dateFormat="yyyy-MM-dd"
                  title={getTranslatedText(
                    intl,
                    'client.details.contract.effectiveDate'
                  )}
                  required
                  disabled={isDisabled}
                />
              </div>
            </Field>
          ) : (
            ''
          )}
          {includes(configurableSections, 'endDate') && // Should not show end date if subscription type is one of EXCLUDE_END_DATE_CATEGORIES
          subscriptionType &&
          !includes(EXCLUDE_END_DATE_CATEGORIES, subscriptionType) ? (
            <Field>
              <EndDate
                intl={intl}
                endDate={endDate}
                minDate={
                  dayjs(billDate).isAfter(dayjs()) ? billDate : new Date()
                }
                // minDate={
                //   !isEmpty(frequency) &&
                //   getMinEndDate({
                //     billDate: billDate,
                //     change: change,
                //     changes: changes,
                //     nextBillDate: nextBillDate,
                //     frequency: frequency,
                //     endDate: endDate,
                //     canEdit: !isDisabled
                //   })
                // }
                remindDuration={remindDuration}
                enableReminder={enableReminder}
                isDisabled={isEditEndDatedisabled}
                handlePropChange={handlePropChange}
                handlePropertiesChange={handlePropertiesChange}
              />
            </Field>
          ) : (
            ''
          )}
        </div>
      </div>
      {configurableSections.indexOf('carryForwardRemainingHours') > -1 &&
        subscriptionType === 'CUSTOM' && (
          <div className="mt-20">
            <Checkbox
              label={getTranslatedText(
                intl,
                'admin.serviceCatalog.carryForwardRemainingHours'
              )}
              checked={carryForwardRemainingBlockHours}
              onChange={(val) => {
                handlePropChange('carryForwardRemainingBlockHours', val);
              }}
              disabled={isDisabled}
            />
          </div>
        )}
      {configurableSections.indexOf('autoRenew') > -1 &&
        subscriptionType === 'PERPETUAL' && (
          <div className="mt-20">
            <Checkbox
              label={getTranslatedText(intl, 'admin.serviceCatalog.autoRenew')}
              checked={autoRenew}
              onChange={(val) => {
                handlePropChange('autoRenew', val);
              }}
              disabled={isDisabled}
            />
          </div>
        )}
      <div>
        <div className="mt-20">
          <Label>
            <FormattedMessage id="admin.serviceCatalog.itemsToDeductFromBlockHours" />
          </Label>
          <div className="w400 mt8">
            <Dropdown
              isClearable
              name="subscriptionTypes"
              value={null}
              onChange={(val) => {
                adjustBlockHourItems(val);
              }}
              options={adjustItems}
              components={{
                IndicatorSeparator,
                DropdownIndicator
              }}
              classNamePrefix="value_selector"
              formatGroupLabel={formatGroupLabel}
              menuPlacement="auto"
              placeholder={getTranslatedText(
                intl,
                'admin.serviceCatalog.chooseItems.placeholder'
              )}
              closeMenuOnScroll={(e) => {
                return (
                  e.target &&
                  e.target.classList &&
                  !(e.target.classList.value.indexOf('menu-list') > -1)
                );
              }}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 })
              }}
              isDisabled={isDisabled}
            />
          </div>
        </div>
        <div className="mt-20">
          {selectedBlockItemAdjustedItems.map((el) => {
            return (
              <div className="flex actr mt-10 visibilityContainer">
                <div>{el.name}</div>
                {!isDisabled && (
                  <div className="ml8 visibilityActions">
                    <Button
                      plain
                      monochrome
                      onClick={deleteAdjustedItem.bind(this, el)}
                      icon={<SVGIcon name="Delete" />}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-20">
        {frequency?.type === 'CUSTOM' &&
          nextBillDateBasedOnBillingType &&
          ((endDate &&
            dayjs(nextBillDateBasedOnBillingType).isSameOrBefore(endDate)) ||
            endDate === null) && (
            <Badge status="attention">
              <span className="fx actr g10 p5">
                <SVGIcon name="About" />
                <FormattedMessage
                  id="client.details.contractinvoice.next.billdate"
                  values={{
                    nextBillDate: nextBillDateLoading ? (
                      <Spinner size="small" />
                    ) : (
                      nextBillDate
                    )
                  }}
                />
              </span>
            </Badge>
          )}
      </div>

      {true && (
        <div className="mt-40">
          <Heading element="h2">
            <FormattedMessage id="contract.serviceItem.quantity.heading" />
          </Heading>
        </div>
      )}
      {changes ? (
        <>
          {changes.map((c) => {
            return (
              <div className="flex g20 mt-10">
                <Field>
                  <Label>
                    <FormattedMessage id="client.contracts.queue.blockHours.quantity" />
                  </Label>

                  <TextField
                    value={String(c.quantity)}
                    type="number"
                    disabled
                    suffix={
                      c.quantityChangeOperation ? (
                        <div className="ptr bshadow flex rounder-4px h-25 cursor-not-allowed">
                          {c.quantityChangeOperation === 'INCREASE' && (
                            <Operation
                              selected={
                                c.quantityChangeOperation === 'INCREASE'
                              }
                            >
                              <SVGIcon
                                name="ArrowDown"
                                color={
                                  c.quantityChangeOperation === 'INCREASE'
                                    ? 'white'
                                    : 'black'
                                }
                              />
                            </Operation>
                          )}
                          {c.quantityChangeOperation === 'DECREASE' && (
                            <Operation
                              type="DECREASE"
                              selected={
                                c.quantityChangeOperation === 'DECREASE'
                              }
                            >
                              <SVGIcon
                                name="ArrowDown"
                                color={
                                  c.quantityChangeOperation === 'DECREASE'
                                    ? 'white'
                                    : 'black'
                                }
                              />
                            </Operation>
                          )}
                        </div>
                      ) : (
                        ''
                      )
                    }
                  />
                </Field>
                {c.effectiveDate && (
                  <Field>
                    <div>
                      <Label>
                        <FormattedMessage id="client.contracts.item.effectiveDate" />
                      </Label>

                      <DatePicker
                        selected={
                          c.effectiveDate && dayjs(c.effectiveDate).toDate()
                        }
                        placeholderText="YYYY-MM-DD"
                        dateFormat="yyyy-MM-dd"
                        title={getTranslatedText(
                          intl,
                          'client.contracts.item.effectiveDate'
                        )}
                        required
                        disabled
                      />
                    </div>
                  </Field>
                )}
                {c.proRatedAmount && (
                  <ProrateWrap className="flex">
                    <div>
                      <Label>
                        <FormattedMessage id="client.contracts.item.proratedAmount" />
                      </Label>

                      <TextField
                        size="extrasmall"
                        value={c.proRatedAmount}
                        min={0}
                        type="number"
                        disabled
                        suffix={`${new Intl.NumberFormat(`en-${countryCode}`, {
                          style: 'currency',
                          currency: currencyCode,
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                          currencyDisplay: 'narrowSymbol'
                        })
                          .format('')
                          .replace(/\d/g, '')
                          .trim()}${currencyCode}`}
                      />
                    </div>
                    <div className="ml-20">
                      <Checkbox
                        label={getTranslatedText(
                          intl,
                          'client.contracts.item.generateInvoice'
                        )}
                        checked={c.generateInvoice}
                        disabled
                      />
                    </div>
                  </ProrateWrap>
                )}
              </div>
            );
          })}
        </>
      ) : (
        ''
      )}
      {renderQuantityGrid()}
    </BlockHourTypeParent>
  );
}

export default injectIntl(NewBlockhourTypeSection);
